/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Student Assignment Performance page
 *
 * @file StudentAssignmentPerformance.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CustomCard from '../common/CustomCard';
import CustomProgressBar from '../common/CustomProgressBar';
import ReviewList from '../common/ReviewList';
import {
  BackButton,
  Root,
  RowSpaceBetween,
  Title,
  Subtitle2,
  CheckIcon,
  ErrorIcon,
  AssignmentNameContainer,
  WrapperCardContainer,
  FlexGrow,
  SecondaryBodyText,
  TopicsCoveredLabel,
  ScrollBox
} from './styles/StudentAssignmentPerformanceStyle';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';
import UnderlineButton from '../common/UnderlineTextButton ';
import Tabular from '../tabular/Tabular';


const StudentAssignmentPerformance = ({
  name,
  assignmentName,
  remainingTime,
  firstAttemptCorrect,
  totalQuestions,
  helpTopics,
  topics,
  status,
  tabalurProps,
  config = {},
  onBackButtonClick,
  backButtonText
}) => {
  const renderBackButton = () => {
    if (!config.showBackButton) return null;

    return (
      <BackButton
        startIcon={<ArrowBackIcon />}
        variant="link"
        onClick={config.showBackButton ? onBackButtonClick : null}
      >
        {backButtonText}
      </BackButton>
    );
  };

  return (
    <Root>
      {renderBackButton()}
      <RowSpaceBetween>
        <Title>{name}</Title>
        <IconButton size="small" aria-label="download">
          <MailOutlineIcon fontSize="small" />
        </IconButton>
      </RowSpaceBetween>
      <RowSpaceBetween>
        <AssignmentNameContainer>
          <Subtitle2>{assignmentName}</Subtitle2>
          {status && (
            <span>
                {status === 'success' && <CheckIcon />}
                {status === 'warning' && <ErrorIcon />}
                {status === 'error' && <ErrorIcon />}
            </span>
          )
          }
        </AssignmentNameContainer>
        <UnderlineButton
          text="Download assignment report"
          onClick={() => {}}
          icon={<CloudDownloadIcon fontSize="small" />}
        />
      </RowSpaceBetween>
      <WrapperCardContainer>
        <FlexGrow>
          <CustomCard backgroundColor="#F6F6FB">
            <Root>
              <div>
                <HtmlTooltip
                  title={<StatusTemplate title="25 Questions total" positiveTitle="12 Correct" negativeTitle="5 Wrong" />}
                  arrow
                  placement="bottom"
                >
                  <div style={{ cursor: 'pointer' }}>
                    <CustomProgressBar value={70} bufferValue={20} gap={4} />
                  </div>
                </HtmlTooltip>
              </div>
              <Root>
                <RowSpaceBetween>
                  <RowSpaceBetween>
                    <AccessTimeIcon fontSize="small" style={{ alignSelf: 'center' }} />
                    <SecondaryBodyText variant="body2">{remainingTime}</SecondaryBodyText>
                  </RowSpaceBetween>
                  <div>
                    <SecondaryBodyText variant="body2">{totalQuestions} questions</SecondaryBodyText>
                  </div>
                </RowSpaceBetween>
                <div>
                  <SecondaryBodyText variant="body2">{firstAttemptCorrect} out of {totalQuestions} questions answered correctly on first attempt</SecondaryBodyText>
                </div>
                <div>
                  <ReviewList list={helpTopics} />
                </div>
              </Root>
            </Root>
          </CustomCard>
        </FlexGrow>
        <FlexGrow>
          <CustomCard backgroundColor="#F6F6FB" style={{ height: '100%' }}>
            <TopicsCoveredLabel>Topics needing review</TopicsCoveredLabel>
            {topics.length > 0 && (
            <ScrollBox>
              {topics.map((topic, index) => (
                <SecondaryBodyText key={index} variant="body2">
                  {topic}
                </SecondaryBodyText>
              ))}
            </ScrollBox>
            )}
          </CustomCard>
        </FlexGrow>
      </WrapperCardContainer>
      {tabalurProps && (
        <div>
          <Tabular
            {...tabalurProps}
          />
        </div>
      )}
    </Root>
  );
};

StudentAssignmentPerformance.propTypes = {
  name: PropTypes.string,
  assignmentName: PropTypes.string,
  remainingTime: PropTypes.string,
  firstAttemptCorrect: PropTypes.number,
  totalQuestions: PropTypes.number,
  helpTopics: PropTypes.array,
  topics: PropTypes.array,
  status: PropTypes.string,
  tabalurProps: PropTypes.shape({
    title: PropTypes.string,
    columns: PropTypes.array,
    data: PropTypes.array
  }),
  config: PropTypes.object,
  onBackButtonClick: PropTypes.func,
  backButtonText: PropTypes.string
};

StudentAssignmentPerformance.defaultProps = {
  name: '',
  assignmentName: '',
  remainingTime: '',
  firstAttemptCorrect: 0,
  totalQuestions: 0,
  helpTopics: [],
  topics: [],
  status: '',
  tabalurProps: null,
  config: {},
  onBackButtonClick: () => {},
  backButtonText: ''
};

export default StudentAssignmentPerformance;
